import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, User, Filter, RefreshCw, Trash2, Pencil, PlusCircle, ArrowRightLeft, Move, Settings as SettingsIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

// Small icon lookup so the timeline reads at a glance.
const ACTION_META = {
  create: { icon: PlusCircle, tint: "text-emerald-600 bg-emerald-50" },
  update: { icon: Pencil, tint: "text-blue-600 bg-blue-50" },
  delete: { icon: Trash2, tint: "text-red-600 bg-red-50" },
  move: { icon: Move, tint: "text-fuchsia-600 bg-fuchsia-50" },
  reorder: { icon: ArrowRightLeft, tint: "text-amber-700 bg-amber-50" },
  ai_apply: { icon: Sparkles, tint: "text-violet-600 bg-violet-50" },
};

function relTime(iso) {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Activity() {
  const [rows, setRows] = useState(null);
  const [source, setSource] = useState("all"); // all | manual | ai
  const [refreshing, setRefreshing] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const q = source === "all" ? "" : `?source=${source}`;
      const { data } = await api.get(`/admin/audit-logs${q}`);
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load activity");
    } finally {
      setRefreshing(false);
    }
  }, [source]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    if (!rows) return { all: 0, ai: 0, manual: 0 };
    return {
      all: rows.length,
      ai: rows.filter((r) => r.source === "ai").length,
      manual: rows.filter((r) => r.source === "manual").length,
    };
  }, [rows]);

  if (rows === null) {
    return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-black/40" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="activity-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Activity</h2>
          <p className="text-xs text-black/50">Every change made to your site — by you, or by the AI. Newest first.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-full border border-black/10 bg-white text-[11px] font-semibold">
            {[
              { key: "all", label: "All", count: counts.all },
              { key: "manual", label: "Manual", icon: User, count: counts.manual },
              { key: "ai", label: "AI", icon: Sparkles, count: counts.ai },
            ].map((f) => {
              const Icon = f.icon;
              const active = source === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setSource(f.key)}
                  data-testid={`activity-filter-${f.key}`}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 transition-colors ${active ? "bg-black text-white" : "text-black/60 hover:bg-black/[0.03]"}`}
                >
                  {Icon && <Icon size={11} />} {f.label}
                  {source === "all" && <span className={`ml-0.5 rounded-full px-1.5 py-0 text-[9px] ${active ? "bg-white/20" : "bg-black/5"}`}>{f.count}</span>}
                </button>
              );
            })}
          </div>
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="activity-refresh"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-black/10 text-black/60 hover:bg-black/[0.03] disabled:opacity-40"
            title="Refresh"
          >
            {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-black/10 py-16 text-center">
          <Filter size={22} className="text-black/20" />
          <p className="mt-2 text-sm font-semibold">No activity yet</p>
          <p className="mt-1 max-w-xs text-xs text-black/40">
            {source !== "all" ? `No ${source} changes in the log.` : "Make a change — add an item, tweak a setting, or ask the AI — and it'll show up here."}
          </p>
        </div>
      ) : (
        <ol className="relative space-y-2 border-l border-black/5 pl-4">
          {rows.map((r) => {
            const meta = ACTION_META[r.action] || { icon: SettingsIcon, tint: "text-black/60 bg-black/5" };
            const Icon = meta.icon;
            const isAI = r.source === "ai";
            const open = expandedId === r.id;
            const hasDetail = !!(r.before || r.after);
            return (
              <li key={r.id} className="relative" data-testid={`activity-row-${r.id}`}>
                <span className={`absolute -left-[22px] top-2 flex h-4 w-4 items-center justify-center rounded-full ring-2 ring-white ${meta.tint}`}>
                  <Icon size={9} />
                </span>
                <button
                  onClick={() => hasDetail && setExpandedId(open ? null : r.id)}
                  disabled={!hasDetail}
                  data-testid={`activity-toggle-${r.id}`}
                  className={`w-full rounded-xl border border-black/5 bg-white p-3 text-left transition-colors ${hasDetail ? "hover:border-black/15" : "cursor-default"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {isAI && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-violet-500/15 to-fuchsia-500/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-fuchsia-700">
                            <Sparkles size={9} /> AI
                          </span>
                        )}
                        <span className="text-[10px] font-bold uppercase tracking-wider text-black/40">{r.entity_type}</span>
                      </div>
                      <p className="mt-1 truncate text-sm font-semibold text-black/85">{r.summary || `${r.action} on ${r.entity_type}`}</p>
                      <p className="mt-1 text-[11px] text-black/40">
                        {r.actor_email || "unknown"} · <span title={r.created_at}>{relTime(r.created_at)}</span>
                      </p>
                    </div>
                  </div>
                  {open && hasDetail && (
                    <div className="mt-3 grid grid-cols-1 gap-2 rounded-lg bg-black/[0.02] p-2 text-[11px] sm:grid-cols-2">
                      {r.before && (
                        <div>
                          <p className="mb-1 font-bold uppercase tracking-wider text-black/40">Before</p>
                          <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-white p-2 font-mono text-[10px] text-black/70">{JSON.stringify(r.before, null, 2)}</pre>
                        </div>
                      )}
                      {r.after && (
                        <div>
                          <p className="mb-1 font-bold uppercase tracking-wider text-black/40">After</p>
                          <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-white p-2 font-mono text-[10px] text-black/70">{JSON.stringify(r.after, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
